import React, { useState, useEffect } from 'react';
import { loadPracticeAnalysis } from '../../../utils/firebase';
import { PRACTICE_MODES } from '../../../constants/modes';
import './SlowestAlgorithms.css';
import HomeButton from '../../common/navigation/HomeButton';
import { useNavigate } from 'react-router-dom';
import AlgorithmStats from '../../common/algorithm/AlgorithmStats';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const SlowestAlgorithms = () => {
  const [slowestAlgs, setSlowestAlgs] = useState({
    [PRACTICE_MODES.OLL.EXECUTION]: [],
    [PRACTICE_MODES.PLL.EXECUTION]: [],
    [PRACTICE_MODES.OLL.RECOGNITION]: [],
    [PRACTICE_MODES.PLL.RECOGNITION]: []
  });
  const [missingCases, setMissingCases] = useState({
    [PRACTICE_MODES.OLL.EXECUTION]: [],
    [PRACTICE_MODES.PLL.EXECUTION]: [],
    [PRACTICE_MODES.OLL.RECOGNITION]: [],
    [PRACTICE_MODES.PLL.RECOGNITION]: []
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { recent, missing } = await loadPracticeAnalysis();
        setSlowestAlgs(recent);
        setMissingCases(missing);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey]);

  useEffect(() => {
    const handleFocus = () => {
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handlePracticeNow = (mode, algorithms) => {
    const path = {
      [PRACTICE_MODES.OLL.EXECUTION]: '/random-oll',
      [PRACTICE_MODES.PLL.EXECUTION]: '/random-pll',
      [PRACTICE_MODES.OLL.RECOGNITION]: '/oll-recognition',
      [PRACTICE_MODES.PLL.RECOGNITION]: '/pll-recognition'
    }[mode];

    navigate(path, { 
      state: { 
        selectedAlgorithms: algorithms.map(alg => alg.id),
        returnPath: '/statistics'
      } 
    });
  };

  const toggleSection = (mode) => {
    setExpandedSections(prev => ({
      ...prev,
      [mode]: !prev[mode]
    }));
  };

  if (loading) return <div>Loading algorithm data...</div>;

  const hasData = Object.values(slowestAlgs).some(algs => algs && algs.length > 0) ||
                 Object.values(missingCases).some(algs => algs && algs.length > 0);
                 
  if (!hasData) return (
    <div>
      <div>No algorithm data available yet</div>
      <HomeButton style={{ marginTop: '20px' }} />
    </div>
  );

  return (
    <div className="slowest-algs-container">
      <div className="header-container">
        <h2 className="page-title">Recent Algorithm Practice</h2>
        <HomeButton />
      </div>
      
      <div className="alg-categories">
        {Object.entries(slowestAlgs).map(([mode, algs]) => (
          <div key={mode} className="alg-category">
            <div className="panel-header">
              <h3>{getModeTitle(mode)}</h3>
              <button 
                className="expand-button"
                onClick={() => toggleSection(mode)}
              >
                {expandedSections[mode] ? (
                  <>
                    <KeyboardArrowUpIcon /> Hide Details
                  </>
                ) : (
                  <>
                    <KeyboardArrowDownIcon /> Show Details
                  </>
                )}
              </button>
            </div>
            <div className="alg-list">
              {algs.length > 0 ? (
                <>
                  <button 
                    className="practice-now-button"
                    onClick={() => handlePracticeNow(mode, algs)}
                  >
                    Practice These Algorithms
                  </button>
                  {algs.map((alg) => (
                    <AlgorithmStats 
                      key={alg.id} 
                      alg={alg} 
                      isExpanded={expandedSections[mode]}
                    />
                  ))}
                </>
              ) : (
                <p className="no-data">No slow algorithms</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <h2 className="section-title">Cases Needing Data</h2>
      <div className="alg-categories">
        {Object.entries(missingCases).map(([mode, algs]) => (
          <div key={mode} className="alg-category">
            <h3>{getModeTitle(mode).replace('Slowest', 'Missing')}</h3>
            <div className="alg-list">
              {algs.length > 0 ? (
                <>
                  <button 
                    className="practice-now-button"
                    onClick={() => handlePracticeNow(mode, algs)}
                  >
                    Practice These Algorithms
                  </button>
                  {algs.map((alg) => (
                    <div key={alg.id} className="missing-alg-item">
                      <span>{alg.name}</span>
                    </div>
                  ))}
                </>
              ) : (
                <p className="no-data">No missing cases</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const getModeTitle = (mode) => {
  switch (mode) {
    case PRACTICE_MODES.OLL.EXECUTION:
      return 'Slowest OLL Execution';
    case PRACTICE_MODES.PLL.EXECUTION:
      return 'Slowest PLL Execution';
    case PRACTICE_MODES.OLL.RECOGNITION:
      return 'Slowest OLL Recognition';
    case PRACTICE_MODES.PLL.RECOGNITION:
      return 'Slowest PLL Recognition';
    default:
      return mode;
  }
};

export default SlowestAlgorithms; 